import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, first, tap } from 'rxjs';
import { indicate } from 'src/app/operators';
import { BranchTableCodeViewModel, EsraPipingAPIClient } from 'src/app/shared/models/autogenerated-piping';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';

@Component({
    selector: 'app-branch-table-code-add-edit',
    templateUrl: './branch-table-code-add-edit.component.html',
    styleUrls: ['./branch-table-code-add-edit.component.scss'],
    standalone: false
})
export class BranchTableCodeAddEditComponent implements OnInit {
  inputFormGroup: FormGroup;
  branchTableCodeViewModel: BranchTableCodeViewModel = new BranchTableCodeViewModel();
  isEditMode: boolean = false;
  title: string = '';
  modalStringReply: string;
  genericMissingValueMessage: string = 'This is a required field';
  submitClicked: boolean = false;
  businessUnits: string[] = ['All'];
  saving$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly dialogRef: MatDialogRef<BranchTableCodeAddEditComponent, boolean>,
    private readonly esraApiClient: EsraPipingAPIClient,
    private readonly snackBarService: SnackBarService,
    private readonly formBuilder: FormBuilder,
    @Inject(MAT_DIALOG_DATA) private readonly branchTableCode: BranchTableCodeViewModel | null
  ) {}

  ngOnInit(): void {
    this.branchTableCodeViewModel = this.branchTableCode
      ? new BranchTableCodeViewModel(this.branchTableCode)
      : new BranchTableCodeViewModel();
    this.isEditMode = this.branchTableCode !== null;
    this.initializeFormControls();
    this.title = this.getTitle();
    this.esraApiClient.getAllBusinessUnits()
      .pipe(
        first(),
        tap(businessUnits =>
          this.businessUnits = this.businessUnits.concat(businessUnits)
        )
      ).subscribe();
  }

  getTitle(): string {
    if (this.isEditMode) {
      return 'Edit branch table code';
    }
    return 'Create branch table code';
  }

  initializeFormControls(): void {
    if (this.isEditMode) {
      this.inputFormGroup = this.formBuilder.group({
        code: new FormControl({ value: this.branchTableCodeViewModel.code, disabled: true }, [Validators.required]),
        description: new FormControl(this.branchTableCodeViewModel.description, [Validators.required]),
      });
    } else {
      this.inputFormGroup = this.formBuilder.group({
        code: new FormControl(this.branchTableCodeViewModel.code, [Validators.required]),
        description: new FormControl(this.branchTableCodeViewModel.description, [Validators.required]),
      });
    }
  }

  save(): void {
    this.submitClicked = true;
    if (this.inputFormGroup.valid) {
      this.updateModelFromForm();

      if (this.isEditMode === false) {
        this.createBranchTableCode();
      }
      else if (this.isEditMode) {
        this.updateBranchTableCode();
      }
    } else {
      this.inputFormGroup.markAllAsTouched();
    }
  }

  updateModelFromForm(): void {
    this.branchTableCodeViewModel.code = this.inputFormGroup?.get('code').value;
    this.branchTableCodeViewModel.description = this.inputFormGroup?.get('description').value;
  }

  private createBranchTableCode(): void {
    this.esraApiClient.createBranchTableCode(this.branchTableCodeViewModel).pipe(
      indicate(this.saving$),
      first()
    ).subscribe({
      next: () => {
        this.snackBarService.showInfo(
          `Branch table code ${this.branchTableCodeViewModel.code} updated successfully`
        );
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.snackBarService.showSnackBar(true, err.response, 'mat-snack-bar-error')
      }
    })
  }

  private updateBranchTableCode(): void {
    this.esraApiClient.updateBranchTableCode(this.branchTableCodeViewModel).pipe(
      indicate(this.saving$),
      first()
    ).subscribe({
      next: () => {
        this.snackBarService.showInfo(
          `Branch table code ${this.branchTableCodeViewModel.code} updated successfully`
        );
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.snackBarService.showSnackBar(true, err.response, 'mat-snack-bar-error')
      }
    });
  }

  protected closeDialog(): void {
    this.dialogRef.close(false);
  }
}
